// Helper variables and functions
const ON_CHANGE_DEBOUNCE_TIMER = 300;

const THEME_EVENTS = {
	cartUpdate: 'cart:updated',
	quantityUpdate: 'quantity:update',
	variantChange: 'variant:change',
	cartError: 'cart:error',
};

function onKeyUpEscape(event) {
	if (event.code.toUpperCase() !== 'ESCAPE') return;

	const openDetailsElement = event.target.closest('details[open]');

	if (!openDetailsElement) return;

	const summaryElement = openDetailsElement.querySelector('summary');

	openDetailsElement.removeAttribute('open');
	summaryElement.setAttribute('aria-expanded', false);
	summaryElement.focus();
}

function debounce(fn, wait) {
	let t;

	return (...args) => {
		clearTimeout(t);
		t = setTimeout(() => fn.apply(this, args), wait);
	};
}

function throttle(fn, delay) {
	let lastCall = 0;

	return function (...args) {
		const now = new Date().getTime();

		if (now - lastCall < delay) {
			return;
		}

		lastCall = now;

		return fn(...args);
	};
}

function fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	};
}

function loadImages(section = false) {
	if (window.Noblesse.enable_image_loading_animation) {
		const images = section
			? section.querySelectorAll('.lazy-image img')
			: document.querySelectorAll('.lazy-image img');

		images.forEach((image) => {
			if (image.complete) {
				image.closest('.lazy-image').classList.add('lazy-image--loaded');
			} else {
				image.onload = () => image.closest('.lazy-image').classList.add('lazy-image--loaded');
			}
		});
	}
}

function autoplayVideo() {
	const videos = document.querySelectorAll('.product-media__video.video');

	videos.forEach((video) => {
		if (video.dataset.autoplay === 'true') {
			const videoObserver = new IntersectionObserver(
				(entries, observer) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							video.querySelector('video').play();
							observer.unobserve(video);
						}
					});
				},
				{ threshold: 0.5 }
			);

			videoObserver.observe(video);
		}
	});
}

function trackViewedProducts() {
	const productDataSelector = document.querySelector('script[data-product-details]');

	if (!productDataSelector) {
		return;
	}

	const localStorageData = JSON.parse(localStorage.getItem('themeViewedProducts')) || [];
	const productData = JSON.parse(productDataSelector.innerHTML);

	if (productData) {
		const productDataExists = localStorageData.findIndex((item) => item.handle === productData.handle);

		if (productDataExists === -1) {
			localStorageData.unshift(productData);
			localStorage.setItem('themeViewedProducts', JSON.stringify(localStorageData));
		} else {
			const duplicatedLocalStorageData = [...localStorageData];
			const temporaryData = duplicatedLocalStorageData[productDataExists];

			duplicatedLocalStorageData.unshift(temporaryData);
			duplicatedLocalStorageData.splice(productDataExists + 1, 1);
			localStorage.setItem('themeViewedProducts', JSON.stringify(duplicatedLocalStorageData));
		}
	}
}

function trapFocus(element) {
	let focusableEls = element.querySelectorAll(
		'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
	);
	let firstFocusableEl = focusableEls[0];
	let lastFocusableEl = focusableEls[focusableEls.length - 1];
	let KEYCODE_TAB = 9;

	firstFocusableEl.focus();

	element.addEventListener('keydown', function (e) {
		let isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

		if (!isTabPressed) {
			return;
		}

		if (e.shiftKey) {
			if (document.activeElement === firstFocusableEl) {
				lastFocusableEl.focus();
				e.preventDefault();
			}
		} else {
			if (document.activeElement === lastFocusableEl) {
				firstFocusableEl.focus();
				e.preventDefault();
			}
		}
	});
}

function setCSSVariables() {
	this.selectors = {
		footer: '.shopify-section-group-footer-group',
		headers: '.shopify-section-group-header-group',
	};

	this.elements = {
		footer: document.querySelectorAll(this.selectors.footer),
		headers: document.querySelectorAll(this.selectors.headers),
	};

	this.headerHeight = 0;
	this.footerHeight = 0;

	if (this.elements.headers.length !== 0) {
		this.elements.headers.forEach((element) => {
			const elementHeight = element.offsetHeight;

			this.headerHeight = this.headerHeight + elementHeight;
		});

		document.body.style.setProperty('--header-height', `${this.headerHeight}px`);
	}

	if (this.elements.footer.length !== 0) {
		this.elements.footer.forEach((element) => {
			const elementHeight = element.offsetHeight;

			this.footerHeight = this.footerHeight + elementHeight;
		});

		document.body.style.setProperty('--footer-height', `${this.footerHeight}px`);
	}
}

const Timer = function (callback, delay) {
	this.timerId = null;
	this.start = null;
	this.remaining = delay;

	this.pause = function () {
		window.clearTimeout(this.timerId);
		this.timerId = null;
		this.remaining -= Date.now() - this.start;
	};

	this.play = function () {
		if (this.timerId) {
			return;
		}

		this.start = Date.now();
		this.timerId = window.setTimeout(callback, this.remaining);
	};

	this.reset = function () {
		this.pause();
		this.start = null;
		this.remaining = null;
		window.clearTimeout(this.timerId);
		this.timerId = null;
	};

	this.play();
};

// Shopify object extensions
Shopify.postLink = function (path, options) {
	options = options || {};
	let method = options['method'] || 'post';
	let params = options['parameters'] || {};
	let form = document.createElement('form');

	form.setAttribute('method', method);
	form.setAttribute('action', path);

	for (let key in params) {
		let hiddenField = document.createElement('input');

		hiddenField.setAttribute('type', 'hidden');
		hiddenField.setAttribute('name', key);
		hiddenField.setAttribute('value', params[key]);
		form.appendChild(hiddenField);
	}
	document.body.appendChild(form);
	form.submit();
	document.body.removeChild(form);
};

// Web components
if (typeof QuantityInput !== 'function') {
	class QuantityInput extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				input: 'input',
				button: 'button',
				minusButton: ".quantity-button[name='minus']",
				plusButton: ".quantity-button[name='plus']",
			};

			this.classes = {
				disabled: 'button--disabled',
			};

			this.input = this.querySelector(this.selectors.input);
			this.buttons = this.querySelectorAll(this.selectors.button);
			this.minusButton = this.querySelector(this.selectors.minusButton);
			this.plusButton = this.querySelector(this.selectors.plusButton);
			this.changeEvent = new Event('change', { bubbles: true });

			this.init();
		}

		init() {
			this.addEventListeners();
			this.validateQtyRules();
		}

		addEventListeners() {
			const { input, buttons } = this;

			input.addEventListener('change', () => {
				this.onInputChange();
			});

			buttons.forEach((button) => {
				button.addEventListener('click', (event) => {
					this.onButtonClick(event);
				});
			});

			document.addEventListener(THEME_EVENTS.quantityUpdate, (event) => {
				this.validateQtyRules();
			});
		}

		onInputChange() {
			this.validateQtyRules();
		}

		onButtonClick(event) {
			event.preventDefault();

			const { input, changeEvent } = this;
			const previousValue = input.value;

			event.target.name === 'plus' ? input.stepUp() : input.stepDown();

			if (previousValue !== input.value) input.dispatchEvent(changeEvent);
		}

		validateQtyRules() {
			const { input, minusButton, plusButton, classes } = this;
			const value = parseInt(input.value);

			if (input.min) {
				const min = parseInt(input.min);

				minusButton.classList.toggle(classes.disabled, value <= min);
			}

			if (input.max) {
				const max = parseInt(input.max);

				plusButton.classList.toggle(classes.disabled, value >= max);
			}
		}
	}

	if (!customElements.get('quantity-input')) {
		customElements.define('quantity-input', QuantityInput);
	}
}

if (typeof AnimatedCircleText !== 'function') {
	class AnimatedCircleText extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				path: 'path',
				text: 'text',
			};

			this.elements = {
				path: this.querySelector(this.selectors.path),
				text: this.querySelector(this.selectors.text),
			};

			this.rotationFactor = this.getAttribute('rotationFactor') || 0.1;
			this.autoplay = this.getAttribute('autoplay') === 'true' ? true : false;

			this.cx = 0;
			this.cy = 0;

			this.init();
		}

		init() {
			this.setSquare();
			this.autoplay ? this.startAutoplay() : this.rotateOnScroll();

			window.addEventListener('scroll', this.scrollHandler, { passive: true });
		}

		calculateCenter() {
			const bbox = this.elements.path.getBBox();

			this.cx = bbox.x + bbox.width / 2;
			this.cy = bbox.y + bbox.height / 2;
		}

		setSquare() {
			this.style.width = this.diameter + 'px';
			this.style.height = this.diameter + 'px';
		}

		rotateOnScroll() {
			window.addEventListener('scroll', () => {
				this.calculateCenter();

				const rotation = window.scrollY * (this.rotationFactor / 100);
				const transform = `rotate(${rotation} ${this.cx} ${this.cy})`;

				this.elements.path.setAttribute('transform', transform);
				this.elements.text.setAttribute('transform', transform);
			});
		}

		startAutoplay() {
			let rotation = 0;

			const rotate = () => {
				rotation += this.rotationFactor / 100;

				this.calculateCenter();

				const transform = `rotate(${rotation} ${this.cx} ${this.cy})`;

				this.elements.path.setAttribute('transform', transform);
				this.elements.text.setAttribute('transform', transform);

				requestAnimationFrame(rotate);
			};

			rotate();
		}

		debounce(func, delay) {
			let timeout;

			return function () {
				const context = this;
				const args = arguments;

				clearTimeout(timeout);

				timeout = setTimeout(() => {
					func.apply(context, args);
				}, delay);
			};
		}

		scrollHandler = this.debounce(this.rotateOnScroll.bind(this), 30);
	}

	if (!customElements.get('animated-circle-text')) {
		customElements.define('animated-circle-text', AnimatedCircleText);
	}
}

if (typeof SocialShare !== 'function') {
	class SocialShare extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				copyClipboardLink: '[data-copy-clipboard]',
			};

			this.classes = {
				success: 'social-share__list-item--success',
			};

			this.copyClipboardLink = this.querySelector(this.selectors.copyClipboardLink);

			this.init();
		}

		init() {
			this.addEventListeners();
		}

		addEventListeners() {
			const { copyClipboardLink } = this;

			if (copyClipboardLink) {
				copyClipboardLink.addEventListener('click', (e) => {
					e.preventDefault();

					this.copyClipboard();
				});
			}
		}

		copyClipboard() {
			const { copyClipboardLink, classes } = this;
			const parent = copyClipboardLink.closest('li');
			const copyText = copyClipboardLink.getAttribute('href');

			let inputElem = document.createElement('input');

			inputElem.type = 'text';
			this.appendChild(inputElem);

			const newInput = this.querySelector('input');

			newInput.value = copyText;
			newInput.select();
			newInput.setSelectionRange(0, 99999);
			document.execCommand('copy');
			this.removeChild(newInput);
			parent.classList.add(classes.success);

			setTimeout(() => {
				parent.classList.remove(classes.success);
			}, 1500);
		}
	}

	if (!customElements.get('social-share')) {
		customElements.define('social-share', SocialShare);
	}
}

  class ParallaxComponent extends HTMLElement {
	constructor() {
	  super();
	  this.maxMovement = parseFloat(this.dataset.max) || 200;
	  this.elements = [];
	}
  
	connectedCallback() {
	  this.elements = Array.from(this.querySelectorAll(".parallax"));

	  if(this.classList.contains('parallax')){
		this.elements.push(this);
	  }

	  this.elements.forEach((el) => this.animateElement(el));
	}
  
	animateElement(el) {
	  const speed = parseFloat(el.getAttribute("speed")) || 0.2;
	  const reverse = el.hasAttribute("reverse") ? -1 : 1;
	  const xAxis = el.hasAttribute("x");
	  const xyAxis = el.hasAttribute("xy");
  
	  let keyframes = [
		{ transform: "translate3d(0, 0, 0)" },
		{ transform: `translate3d(${xAxis || xyAxis ? this.maxMovement * reverse * speed : 0}px, ${xyAxis ? this.maxMovement * reverse * speed : this.maxMovement * reverse * speed}px, 0)` },
		{ transform: "translate3d(0, 0, 0)" }
	  ];
  
	  el.animate(keyframes, {
		duration: 8000,
		iterations: Infinity,
		easing: "ease-in-out"
	  });
	}
  }
  
  
  if (!customElements.get("parallax-component")) {
	customElements.define("parallax-component", ParallaxComponent);
  }

if (typeof TabsSliders !== 'function') {
	class TabsSliders extends HTMLElement {
		constructor() {
			super();

			this.classes = {
				active: 'active',
				hidden: 'hidden',
			};

			this.selectors = {
				tabs: '.tabs-content__headings li',
				contents: '.tabs-content__content',
			};

			this.tabs = this.querySelectorAll(this.selectors.tabs);
			this.contents = this.querySelectorAll(this.selectors.contents);

			if (this.tabs.length > 0) {
				this.init();
			}
		}

		init() {
			this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick.bind(this)));
			this.tabs.forEach((tab) =>
				tab.addEventListener('keyup', (e) => {
					('Space' !== e.code && 'Enter' !== e.code) || this.onTabClick(e);
				})
			);
			this.addEventListener('tabs-sliders:destroy', this.destroy.bind(this));
		}

		destroy(event) {
			const { tab } = event.detail;

			this.tabs.forEach((element, index) => {
				if (parseInt(element.dataset.tab) === parseInt(tab)) {
					element.remove();
				} else {
					element.classList.add(this.classes.active);
				}
			});

			this.contents.forEach((content, index) => {
				if (parseInt(content.dataset.tab) === parseInt(tab)) {
					content.remove();
				} else {
					content.classList.remove(this.classes.hidden);
				}
			});

			this.checkVisibility();
		}

		checkVisibility() {
			if (!this.querySelector(this.selectors.tabs)) {
				this.classList.add(this.classes.hidden);
			}
		}

		onTabClick(event) {
			const target = event.target.dataset.tab ? event.target : event.target.closest('[data-tab]');
			const tabNumber = target.dataset.tab;

			this.tabs.forEach((tab) => {
				tab.dataset.tab === tabNumber
					? tab.classList.add(this.classes.active)
					: tab.classList.remove(this.classes.active);
			});

			this.contents.forEach((content) => {
				content.dataset.tab === tabNumber
					? content.classList.remove(this.classes.hidden)
					: content.classList.add(this.classes.hidden);
			});
		}
	}

	if (!customElements.get('tabs-sliders')) {
		customElements.define('tabs-sliders', TabsSliders);
	}
}

if (typeof CollectionSlider !== 'function') {
	class CollectionSlider extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				splide: '.splide',
				splideList: '.splide__list',
				splideSlide: '.splide__slide',
				tabsSliders: 'tabs-sliders',
				tabsContent: '.tabs-content__content',
				noSibling: '[data-recommended-products="false"]',
				secondImage: '.product-media__second-image',
				productItem: '.product-item',
			};

			this.classes = {
				paginationEnabled: 'collection-slider--pagination-enabled',
				slide: 'splide__slide',
				isCentered: 'is-centered',
			};

			this.recentlyViewed = this.dataset.recentlyViewed === 'true' ? true : false;
			this.recommendedProducts = this.dataset.recommendedProducts === 'true' ? true : false;
			this.complementaryProducts = this.dataset.complementaryProducts === 'true' ? true : false;
			this.splideElement = this.querySelector(this.selectors.splide);
			this.splideList = this.splideElement.querySelector(this.selectors.splideList);
			this.blocksOnDesktop = parseInt(this.splideElement.dataset.blocksOnDesktop);
			this.blocksOnMobile = parseInt(this.splideElement.dataset.blocksOnMobile);
			this.displayArrows = this.splideElement.dataset.displayArrows === 'true' ? true : false;
			this.promoPosition = this.splideElement.dataset.promoBlockPosition;
			this.numberOfBlocks = this.splideElement.querySelectorAll(this.selectors.splideSlide).length;
			this.splide = {};

			if (window.Shopify.designMode) {
				document.addEventListener('shopify:section:load', async () => {
					await this.init();
				});
			}
		}

		async connectedCallback() {
			await this.init();
		}

		async init() {
			if (this.recentlyViewed) {
				await this.createSlides();
			}

			this.splide = new Splide(this.splideElement, {
				rewind: false,
				pagination: this.complementaryProducts ? true : false,
				speed: 500,
				perPage: this.blocksOnDesktop,
				arrows: this.displayArrows,
				gap: 20,
				drag: this.numberOfBlocks < this.blocksOnDesktop ? false : 'free',
				snap: true,
				breakpoints: {
					1024: {
						perPage: 3,
						drag: this.numberOfBlocks < 3 ? false : 'free',
					},
					768: {
						perPage: this.blocksOnMobile,
						drag: this.numberOfBlocks < this.blocksOnMobile ? false : 'free',
					},
					640: {
						perPage: this.promoPosition == 'fixed' ? 1 : this.blocksOnMobile,
						drag: this.numberOfBlocks < this.blocksOnMobile ? false : 'free',
						gap: '10px',
						arrows: false,
						pagination: this.numberOfBlocks <= this.blocksOnMobile ? false : true,
					},
				},
			});

			if (this.displayArrows) {
				this.splide.on('visible', (slide) => this.modifyErrors(slide));
			}

			if (this.recommendedProducts) {
				const imagesEvent = new CustomEvent('lazy-image:reload', {
					bubbles: true,
					detail: this,
				});
				document.dispatchEvent(imagesEvent);
			}

			this.splide.on('resized', debounce(this.adaptStyle.bind(this), 100));
			this.splide.on('mounted', debounce(this.adaptStyle.bind(this), 100));
			this.splide.mount();
		}

		async createSlides() {
			const themeViewedProducts = JSON.parse(localStorage.getItem('themeViewedProducts'));

			this.splideList.innerHTML = '';

			if (!themeViewedProducts) {
				this.closest(this.selectors.noSibling)
					? (this.closest(this.selectors.noSibling).style.padding = '0')
					: '';
				this.removeSliderFromTabs();

				return;
			}

			const pathNameEntries = new URL(window.location.href).pathname.split('/');
			const arrayOfHandles = themeViewedProducts
				.map((prod) => prod.handle)
				.filter((handle, index) => pathNameEntries.includes(handle) === false)
				.filter((handle, index) => index < parseInt(this.dataset.rvLimit));
			const responses = await Promise.all(
				arrayOfHandles.map((prod, index) =>
					fetch(`${window.routes.root}products/${prod}?section_id=api-product-grid-item`)
				)
			);
			let texts = await Promise.all(responses.map((res) => res.text()));

			if (texts.some((text) => text === '')) {
				texts = texts.filter((text) => text !== '');
			}

			if (texts.length === 0) {
				this.removeSliderFromTabs();

				return;
			}

			const slides = texts.map((product) => {
				const slide = document.createElement('li');
				const content = new DOMParser().parseFromString(product, 'text/html');
				slide.classList.add(this.classes.slide);
				slide.append(content.querySelector(this.selectors.productItem));
				slide
					.querySelectorAll('picture, .video')
					.forEach((picture) => picture.classList.add(this.dataset.rvRatio));

				if (this.dataset.rvSecondImage === 'false' && slide.querySelector(this.selectors.secondImage)) {
					slide.querySelector(this.selectors.secondImage).outerHTML = '';
				}

				return slide;
			});

			if (slides.length === 0) {
				this.removeSliderFromTabs();

				return;
			}

			this.numberOfBlocks = slides.length;
			this.splideList.append(...slides);
		}

		modifyErrors(slide) {
			if (
				slide.index === this.splide.length - 1 &&
				this.splide.Components &&
				this.splide.Components.Arrows &&
				this.splide.Components.Arrows.arrows &&
				this.splide.Components.Arrows.arrows.next
			) {
				if (!this.splide.Components.Arrows.arrows.next.disabled) {
					this.splide.Components.Arrows.arrows.next.disabled = true;
				}
			}

			if (
				slide.index === 0 &&
				this.splide.Components &&
				this.splide.Components.Arrows &&
				this.splide.Components.Arrows.arrows &&
				this.splide.Components.Arrows.arrows.prev
			) {
				if (!this.splide.Components.Arrows.arrows.prev.disabled) {
					this.splide.Components.Arrows.arrows.prev.disabled = true;
				}
			}
		}

		adaptStyle() {
			if (this.splide.Components.Pagination.items.length != 0) {
				this.classList.add(this.classes.paginationEnabled);
			} else {
				this.classList.remove(this.classes.paginationEnabled);
			}

			const lengthOfSlides = this.splide.Components.Elements.slides.length;
			const blocksToDisplay = this.splide.options.perPage;

			if (lengthOfSlides < blocksToDisplay) {
				this.splide.root.classList.add(this.classes.isCentered);
			} else {
				this.splide.root.classList.remove(this.classes.isCentered);
			}
		}

		removeSliderFromTabs() {
			const tabsSliders = this.closest(this.selectors.tabsSliders);
			const tabsContent = this.closest(this.selectors.tabsContent);

			if (tabsSliders) {
				tabsSliders.dispatchEvent(
					new CustomEvent('tabs-sliders:destroy', { bubbles: true, detail: { tab: tabsContent.dataset.tab } })
				);
			}
		}
	}

	if (!customElements.get('collection-slider')) {
		customElements.define('collection-slider', CollectionSlider);
	}
}

if (typeof ProductRecommendations !== 'function') {
	class ProductRecommendations extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				sectionClass: `.${this.dataset.sectionClass}`,
				productItem: '.product-item',
				tabsSliders: 'tabs-sliders',
				tabsContent: '.tabs-content__content',
				productTab: '.product-tab',
				productUpsell: '.product-info__upsell',
			};

			this.classes = {
				loaded: 'product-recommendations--loaded',
				hideTabHeading: 'product-tab--hide-heading',
				upsellLoaded: 'product-info__upsell--loaded',
			};

			this.init();

			if (window.Shopify.designMode) {
				document.addEventListener('shopify:section:load', () => {
					this.init();
				});
			}
		}

		init() {
			const handleIntersection = (entries, observer) => {
				if (!entries[0].isIntersecting) return;
				observer.unobserve(this);

				fetch(this.dataset.url)
					.then((response) => response.text())
					.then((text) => {
						const content = new DOMParser().parseFromString(text, 'text/html');
						const recommendations = content.querySelector(this.selectors.sectionClass);

						if (recommendations && recommendations.innerHTML.trim().length) {
							this.innerHTML = recommendations.innerHTML;
						}

						if (recommendations.querySelector(this.selectors.productItem)) {
							this.classList.add(this.classes.loaded);

							const productTab = this.closest(this.selectors.productTab);
							const productUpsell = this.closest(this.selectors.productUpsell);
							if (productTab) productTab.classList.remove(this.classes.hideTabHeading);
							if (productUpsell) productUpsell.classList.add(this.classes.upsellLoaded);

							const imagesEvent = new CustomEvent('lazy-image:reload', {
								bubbles: true,
								detail: this,
							});

							document.dispatchEvent(imagesEvent);
						} else {
							const tabsSliders = this.closest(this.selectors.tabsSliders);
							const tabsContent = this.closest(this.selectors.tabsContent);

							if (tabsSliders) {
								tabsSliders.dispatchEvent(
									new CustomEvent('tabs-sliders:destroy', {
										bubbles: true,
										detail: { tab: tabsContent.dataset.tab },
									})
								);
							}
						}
					})
					.catch((e) => {
						console.error(e);
					});
			};

			new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
		}
	}

	if (!customElements.get('product-recommendations')) {
		customElements.define('product-recommendations', ProductRecommendations);
	}
}

// Global Classes
class LocalisationForm {
	constructor(self) {
		this.selectors = {
			form: '.localisation-form',
			select: '.localisation-form__select',
			input: '.localisation-form__input',
		};

		this.classes = {
			loading: 'form--loading',
		};

		this.init(self);
	}

	init(form) {
		if (!form) return;

		const select = form.querySelector(this.selectors.select);
		const input = form.querySelector(this.selectors.input);

		select.addEventListener('change', () => {
			form.classList.add(this.classes.loading);
			input.value = select.value;
			form.submit();
		});
	}
}

// Global listeners
window.addEventListener('load', () => {
	autoplayVideo();
	document.querySelectorAll('.localisation-form').forEach((form) => new LocalisationForm(form));
});

document.addEventListener('DOMContentLoaded', () => {
	trackViewedProducts();
	setCSSVariables();
});

document.addEventListener('lazy-image:reload', (event) => loadImages(event.detail.container));
document.addEventListener('videos:reload', (event) => autoplayVideo(event.detail.container));
document.addEventListener('recently-viewed:clear', () => localStorage.removeItem('themeViewedProducts'));
